/**
 * XrefPanel — the External References palette (Faz 5.10, Y21; OCS
 * xref_manager.rs parity): Reference Name · Status · File path · Attached
 * date for every CAD link in the document, with Reload / Detach / Open
 * file location actions and the stale badge (source file modified after
 * the last parse).
 *
 * Status semantics:
 *  - Loaded: a session File handle exists in the CadLinkStore.
 *  - Not found: the session handle is gone (app restart) — Reload falls
 *    back to the file picker.
 *  - Stale: the file's lastModified is newer than the last parse.
 */

import {
    Badge,
    Box,
    Flex,
    IconButton,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { FolderOpen, RefreshCw, Unlink } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sharedLinkManager, type LinkInstance } from '../../core/LinkManager';
import { CadLinkStore } from '../../services/cad/CadLinkStore';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { applyParsedGeometryToElement, pickCadFile, readCadFile } from '../../tools/LinkCADTool';
import { notify } from '../../utils/notify';

/** Best-effort "open file location" through the Tauri shell (the desktop
 *  host injects __TAURI__; the plain browser fallback only reports). */
async function openFileLocation(path: string): Promise<void> {
    const tauri = (globalThis as Record<string, unknown>).__TAURI__ as {
        shell?: { open?: (p: string) => Promise<void> };
        core?: { invoke?: (cmd: string, args?: Record<string, unknown>) => Promise<void> };
    } | undefined;
    try {
        if (tauri?.shell?.open) {
            await tauri.shell.open(path);
            return;
        }
        if (tauri?.core?.invoke) {
            await tauri.core.invoke('plugin:opener|open_path', { path });
            return;
        }
    } catch { /* fall through to the honest no-op */ }
    notify(`File location: ${path}`, 'info');
}

type XrefStatus = 'loaded' | 'unloaded' | 'notFound';

interface XrefRow {
    link: LinkInstance;
    status: XrefStatus;
    stale: boolean;
    /** Attachment timestamp (the link registry's creation stamp). */
    attachedAt: number;
}

export function XrefPanel() {
    const { t } = useTranslation();
    // The LinkManager is a plain class with a listener set, not a zustand
    // store: subscribe manually and bump a version to re-derive. The BIM
    // elements map rides along (a kernel refresh swaps its identity).
    const [version, setVersion] = useState(0);
    useEffect(() => sharedLinkManager.subscribe(() => setVersion((v) => v + 1)), []);
    const elements = useBIMStore((s) => s.project.elements);

    const rows = useMemo<XrefRow[]>(() => {
        void elements;
        // CAD links + PDF underlay references (Faz 9.8): native CAD imports
        // register PDFUNDERLAY/IMAGE sources as 'PDF' links; their session
        // handle is never resolved at import, so they show the missing
        // (notFound) badge until the file is re-attached.
        return sharedLinkManager.getAllLinks()
            .filter((l) => l.type === 'CAD' || l.type === 'PDF')
            .map((link) => {
                const source = link.documentId ? CadLinkStore.getSource(link.documentId) : undefined;
                const status: XrefStatus = source?.file
                    ? 'loaded'
                    : link.status === 'Unloaded'
                        ? 'unloaded'
                        : 'notFound';
                const stale = !!source?.file && source.file.lastModified > source.lastLoaded;
                return { link, status, stale, attachedAt: link.lastModified };
            });
    }, [version, elements]);

    const reload = useCallback(async (row: XrefRow) => {
        const docId = row.link.documentId;
        if (row.link.type !== 'CAD' || !docId) {
            // PDF underlay references (Faz 9.8) have no resolved session
            // handle: re-attaching happens through PDFATTACH, which places
            // the real raster; the palette row stays the reference ledger.
            notify(t('ui.cad.xref.reloadUnsupported'), 'warning');
            return;
        }
        let file = CadLinkStore.getSource(docId)?.file ?? null;
        if (!file) {
            notify(t('ui.cad.xref.sourceLost'), 'warning');
            file = await pickCadFile();
            if (!file) return;
        }
        try {
            const result = await readCadFile(file);
            applyParsedGeometryToElement(docId, file, result, useBIMStore.getState());
            CadLinkStore.markLoaded(docId);
            sharedLinkManager.reloadLink(row.link.id);
            notify(t('ui.cad.xref.reloaded', { name: row.link.name, n: result.lines.length }), 'success');
        } catch (err) {
            notify(t('ui.cad.xref.reloadFailed', { message: (err as Error).message }), 'error');
        }
        setVersion((v) => v + 1);
    }, [t]);

    const detach = useCallback((row: XrefRow) => {
        const bim = useBIMStore.getState();
        const docId = row.link.documentId;
        if (docId) {
            bim.removeElement(docId);
            useAppStore.getState().hideElementsInView([docId]);
        }
        CadLinkStore.removeSource(docId ?? row.link.id);
        sharedLinkManager.removeLink(row.link.id);
        notify(t('ui.cad.xref.detached', { name: row.link.name }), 'info');
        setVersion((v) => v + 1);
    }, [t]);

    return (
        <Box h="full" display="flex" flexDirection="column" minW={0}>
            {/* Column header */}
            <Flex px={2} py={1.5} gap={2} borderBottom="1px solid" borderColor="border.base" flexShrink={0}>
                <Text fontSize="10px" color="text.secondary" textTransform="uppercase" flex="1" minW={0}>
                    {t('ui.cad.xref.colName')}
                </Text>
                <Text fontSize="10px" color="text.secondary" textTransform="uppercase" w="86px" flexShrink={0}>
                    {t('ui.cad.xref.colStatus')}
                </Text>
                <Text fontSize="10px" color="text.secondary" textTransform="uppercase" w="92px" flexShrink={0}>
                    {t('ui.cad.xref.colAttached')}
                </Text>
                <Text fontSize="10px" color="text.secondary" textTransform="uppercase" w="72px" flexShrink={0}>
                    {t('ui.cad.xref.colActions')}
                </Text>
            </Flex>

            <Box flex="1" minH="0" overflowY="auto" overflowX="hidden">
                {rows.length === 0 ? (
                    <Text px={2} py={4} fontSize="xs" color="text.secondary" textAlign="center">
                        {t('ui.cad.xref.empty')}
                    </Text>
                ) : (
                    <VStack spacing={0} align="stretch" px={1}>
                        {rows.map((row) => (
                            <Flex key={row.link.id} px={1} py={1.5} gap={2} align="center"
                                _hover={{ bg: 'bg.hover' }} borderRadius="4px">
                                <VStack spacing={0} align="stretch" flex="1" minW={0}>
                                    <Text fontSize="xs" color="text.primary" noOfLines={1}>{row.link.name}</Text>
                                    <Text fontSize="10px" color="text.secondary" noOfLines={1} title={row.link.path}>
                                        {row.link.path}
                                    </Text>
                                </VStack>
                                <Box w="86px" flexShrink={0}>
                                    <Badge
                                        size="sm"
                                        colorScheme={row.status === 'loaded' ? 'green' : row.status === 'unloaded' ? 'orange' : 'red'}
                                    >
                                        {t(`ui.cad.xref.status_${row.status}`)}
                                    </Badge>
                                    {row.stale && (
                                        <Tooltip label={t('ui.cad.xref.staleTip')} hasArrow>
                                            <Badge size="sm" colorScheme="yellow" ml={1}>
                                                {t('ui.cad.xref.stale')}
                                            </Badge>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Text fontSize="10px" color="text.secondary" w="92px" flexShrink={0} noOfLines={1}>
                                    {new Date(row.attachedAt).toLocaleDateString()}
                                </Text>
                                <Flex w="72px" justify="flex-end" gap={0} flexShrink={0}>
                                    <Tooltip label={t('ui.cad.xref.reload')} hasArrow>
                                        <IconButton size="xs" variant="ghost" aria-label={t('ui.cad.xref.reload')}
                                            icon={<RefreshCw size={12} />} onClick={() => void reload(row)} />
                                    </Tooltip>
                                    <Tooltip label={t('ui.cad.xref.detach')} hasArrow>
                                        <IconButton size="xs" variant="ghost" aria-label={t('ui.cad.xref.detach')}
                                            icon={<Unlink size={12} />} onClick={() => detach(row)} />
                                    </Tooltip>
                                    <Tooltip label={t('ui.cad.xref.openLocation')} hasArrow>
                                        <IconButton size="xs" variant="ghost" aria-label={t('ui.cad.xref.openLocation')}
                                            icon={<FolderOpen size={12} />} onClick={() => void openFileLocation(row.link.path)} />
                                    </Tooltip>
                                </Flex>
                            </Flex>
                        ))}
                    </VStack>
                )}
            </Box>
        </Box>
    );
}
